import Pikaday from 'pikaday';
import { formatNativeDate, formatStringDate, PMNDate, addDays, toDate } from './dateTime';

/**
 * We perform date formatting in multiple places which should be kept in sync:
 * - `PMN.Helpers.DateTimeFormatter.format_date/1`
 * - `static/js/utils/datepicker.js`
 *
 * The DATEPICKER_FORMAT is what's rendered in the Datepicker control.
 * It should match in output to `client_format` defined in
 * PMN.Helpers.DateTimeFormatter
 *
 * The FRIENDLY_FORMAT is the one we use to display computed end dates, based on
 * selected start dates.
 *
 * The SERVER_FORMAT is the format the associated hidden input value should be
 * set to.
 */
const DATEPICKER_FORMAT = 'dddd, MMMM Do, YYYY';
const FRIENDLY_FORMAT = 'dddd, MMMM Do';
const SERVER_FORMAT = 'YYYY-MM-DD';

export const formatDate = (date: Date): string => formatNativeDate(date, FRIENDLY_FORMAT);

type PickerOptions = {
  onSelect?: (
    date: Date,
    picker: Pikaday,
    hiddenInput: HTMLInputElement,
    options: PickerOptions,
  ) => void;
};

export type StartDate = PMNDate;
export type DatePicker = {
  picker: Pikaday;
  field: HTMLInputElement;
  reset: () => void;
};

const computeEndDate = (date: Date | string, dayCount: number): Date => addDays(date, dayCount);

const createEvent = (name: string) => {
  const e = document.createEvent('Event');
  e.initEvent(name, true, true);
  return e;
};

const setValue = (input: HTMLInputElement, value: string): void => {
  input.value = value;
  input.dispatchEvent(createEvent('change'));
};

const setFieldFromHiddenInput = (field: HTMLInputElement, hiddenInput: HTMLInputElement) => {
  if (hiddenInput.value) {
    field.value = formatStringDate(hiddenInput.value, DATEPICKER_FORMAT);
  }
};

const initializeDatepicker = (element: HTMLElement, options: PickerOptions): DatePicker => {
  const field = element.querySelector<HTMLInputElement>('input[type=text]');

  if (!field) {
    throw new Error('No input found');
  }

  const hiddenInput = element.querySelector<HTMLInputElement>('input[type=hidden]');

  if (!hiddenInput) {
    throw new Error('No associated hidden input found found');
  }

  setFieldFromHiddenInput(field, hiddenInput);

  const picker = new Pikaday({
    field,
    format: DATEPICKER_FORMAT,
    // Set the minimum date to one defined by the backend. This could be tomorrow or the day after,
    // depending on the current date in the System and user's timezones.
    minDate:
      field.dataset.minDate && field.dataset.minDate.length > 0
        ? toDate(field.dataset.minDate)
        : undefined,
    onSelect(date: Date) {
      setValue(hiddenInput, formatNativeDate(date, SERVER_FORMAT));

      if (options.onSelect) {
        options.onSelect(date, this, hiddenInput, options);
      }
    },
    parse: (value: string): Date => {
      // input contains an ordinal string and is formatted as DATEPICKER_FORMAT
      // parsing it requires us to replace the parts incompatible with dayjs, so
      // they can be parsed into a date

      // discard the first part, which is the full day name
      const [, ...rest] = value.split(', ');

      return toDate(
        // replace the ordinal day of month with non-ordinal
        rest.join(', ').replace('st', '').replace('th', '').replace('nd', ''),
        'MMMM dd, YYYY',
      );
    },
    toString: formatDate,
  });

  /**
   * Pikaday does not deal with clearing out the date input manually using
   * keyboard, i.e. onSelect is not called, so we manually unset the
   * associated hidden input.
   */
  field.addEventListener('change', () => {
    if (field.value === '') {
      setValue(hiddenInput, '');
    }
  });

  hiddenInput.addEventListener('change', () => {
    const hiddenValue = hiddenInput.value;
    const pickerDate = picker.getDate();
    const pickerValue = pickerDate ? formatNativeDate(pickerDate, SERVER_FORMAT) : '';
    if (hiddenValue !== pickerValue) {
      picker.setDate(hiddenValue);
    }
  });

  /**
   * Generally, pikaday supports a `trigger` option to specify an element
   * other than the default input as the "trigger" to open the datepicker.
   *
   * However, specifiying that option means the datepicker no longer opens on
   * its own when focusing the input field.
   *
   * Because of that, we bind to the click on the trigger manually
   */
  const trigger = element.querySelector<HTMLElement>('.datepicker__trigger');

  if (trigger) {
    trigger.addEventListener('click', () => {
      picker.show();
    });
  }

  /**
   * A picker element can optionally contain a 'clear' button which unsets
   * the date.
   */
  const clearer: HTMLElement | null = element.querySelector('.datepicker__clear');
  if (clearer) {
    clearer.addEventListener('click', () => {
      picker.setDate(null);
      setValue(hiddenInput, '');
    });
  }

  const pickerValue = picker.getDate();
  if (!hiddenInput.value && pickerValue) {
    setValue(hiddenInput, formatNativeDate(pickerValue, SERVER_FORMAT));
  }

  return {
    picker,
    field,
    reset: () => setFieldFromHiddenInput(field, hiddenInput),
  };
};

const initializeDatepickers = (options: PickerOptions = {}): DatePicker[] => {
  const elements = document.querySelectorAll<HTMLElement>('.datepicker');

  const pickers = [];

  for (let i = 0; i < elements.length; i += 1) {
    const el = elements[i];
    const picker = initializeDatepicker(el, options);

    pickers.push(picker);
  }

  return pickers;
};

export default initializeDatepickers;
export { initializeDatepickers, computeEndDate };
